import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "react-i18next";

interface LegalDoc {
  slug: string;
  version: number;
  title: string;
  content_md: string;
  published_at: string;
}

/** Tags/attributes the legal documents are allowed to produce. */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["h1", "h2", "h3", "p", "ul", "li", "strong", "em", "code", "a", "br"],
  ALLOWED_ATTR: ["class", "href", "target", "rel"],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
} as const;

/**
 * Renders markdown to HTML, then runs the result through DOMPurify (a vetted
 * sanitizer) with a strict allowlist. The renderer escaping alone is not the
 * security boundary — DOMPurify is.
 */
export function renderLegalMarkdown(md: string): string {
  const html = markdownToHtml(md);
  if (typeof window === "undefined") return html; // SSR: sanitized again on hydrate
  return String(DOMPurify.sanitize(html, SANITIZE_CONFIG as never));
}

function markdownToHtml(md: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  const out: string[] = [];
  let inList = false;
  let inPara = false;
  const closePara = () => {
    if (inPara) {
      out.push("</p>");
      inPara = false;
    }
  };
  const closeList = () => {
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
  };
  const inline = (s: string) =>
    esc(s)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(
        /\[([^\]]+)\]\((https?:[^)]+|mailto:[^)]+)\)/g,
        '<a class="underline" href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      );
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      closePara();
      closeList();
      continue;
    }
    let m: RegExpMatchArray | null;
    if ((m = line.match(/^(#{1,3})\s+(.*)$/))) {
      closePara();
      closeList();
      const level = m[1].length;
      const cls = level === 1 ? "mb-4 text-3xl font-bold" : "mt-6 mb-2 text-xl font-semibold";
      out.push(`<h${level} class="${cls}">${inline(m[2])}</h${level}>`);
      continue;
    }
    if ((m = line.match(/^\s*-\s+(.*)$/))) {
      closePara();
      if (!inList) {
        out.push('<ul class="list-disc space-y-1 pl-6">');
        inList = true;
      }
      out.push(`<li>${inline(m[1])}</li>`);
      continue;
    }
    closeList();
    if (!inPara) {
      out.push('<p class="mb-3">');
      inPara = true;
    } else {
      out.push(" ");
    }
    out.push(inline(line));
  }
  closePara();
  closeList();
  return out.join("\n");
}

export function LegalDocumentPage({ slug, fallbackTitle }: { slug: string; fallbackTitle: string }) {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const { t } = useTranslation();
  const html = useMemo(() => (doc ? renderLegalMarkdown(doc.content_md) : ""), [doc]);
  const [isTranslated, setIsTranslated] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const load = async (s: string) => {
        const { data } = await supabase.rpc("get_published_legal" as never, { _slug: s } as never);
        return (data as LegalDoc | null) ?? null;
      };
      // Prefer a localized document (e.g. "privacy.es"); fall back to the
      // canonical English text so legal pages never render empty.
      let found: LegalDoc | null = null;
      let localized = true;
      if (language.code !== "en") {
        found = await load(`${slug}.${language.code}`);
        if (!found) localized = false;
      }
      if (!found) found = await load(slug);
      if (cancelled) return;
      setDoc(found);
      setIsTranslated(localized);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, language.code]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <Link to="/" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> {t("nav.home")}
      </Link>
      {!loading && doc && !isTranslated && (
        <p className="mb-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {t("legal.englishOnly")}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : doc ? (
        <>
          <p className="mb-2 text-xs text-muted-foreground">
            Version {doc.version} · Last updated {new Date(doc.published_at).toLocaleDateString()}
          </p>
          <article
            className="prose prose-sm max-w-none text-sm leading-relaxed text-foreground"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </>
      ) : (
        <>
          <h1 className="mb-4 text-3xl font-bold">{fallbackTitle}</h1>
          <p className="text-sm text-muted-foreground">
            This document is being updated. Please check back shortly or email{" "}
            <a className="underline" href="mailto:info@inquira.online">
              info@inquira.online
            </a>
            .
          </p>
        </>
      )}
      <footer className="mt-10 border-t border-border pt-4 text-xs text-muted-foreground">
        Questions about this document? Contact us at{" "}
        <a className="underline" href="mailto:info@inquira.online">
          info@inquira.online
        </a>
        .
      </footer>
    </main>
  );
}